import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  Loader2,
  Download,
  ArrowLeft,
  FileText,
  Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { DashboardLayout } from '../components/DashboardLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { reportsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { convertAmount } from '../lib/currencyConvert';
import { format } from 'date-fns';
import { TRANSACTION_CATEGORY_LABELS as categoryLabels, TRANSACTION_CATEGORY_MAP } from '../lib/transactionCategory';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

const formatAmount = (v, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  return `${sym}${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const signedAmount = (v, currency = 'GBP') => `${v >= 0 ? '+' : ''}${formatAmount(v, currency)}`;

const categoryColors = Object.fromEntries(
  Object.entries(TRANSACTION_CATEGORY_MAP).map(([key, v]) => [key, v.color]),
);

const statusColors = {
  paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  partially_paid: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  unpaid: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const entityLabelsMap = {
  all: 'All Entities',
  br_ltd: 'BR Ltd',
  eci: 'ECI',
  bi_ltd: 'BI Ltd',
  fan_zhang: 'Fan Zhang',
  fan_zhang_piotr_kacki: 'FZ & PK',
  piotr_kacki: 'Piotr Kacki',
};

function SummaryCard({ label, value, icon: Icon, iconBg, valueClass }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', iconBg)}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-xl font-bold font-mono', valueClass)}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function MonthReportPage() {
  const { year: yearParam, month: monthParam } = useParams();
  const navigate = useNavigate();
  const { selectedEntity, entities } = useEntityStore();
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);

  const year = parseInt(yearParam, 10);
  const month = parseInt(monthParam, 10);

  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const entityId = selectedEntity !== 'all' ? selectedEntity : undefined;

  useEffect(() => {
    const fetchDetail = async () => {
      setIsLoading(true);
      setDetail(null);
      try {
        const { data } = await reportsAPI.getMonthDetail(year, month, { entity_id: entityId });
        setDetail(data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    };
    if (entities.length > 0 || selectedEntity === 'all') {
      fetchDetail();
    }
  }, [year, month, selectedEntity, entities]);

  const goMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    navigate(`/reporting/${y}/${m}`);
  };

  const reportCurrency = detail?.currency || 'GBP';
  const fmt = (v) => formatAmount(v, reportCurrency);
  const signedFmt = (v) => signedAmount(v, reportCurrency);

  // Summary cards only: this endpoint's figures are always natively GBP, so
  // convert via the captured fx_rates_used before formatting in the display
  // currency. Per-invoice/per-transaction rows keep their own native currency
  // and must not go through this path.
  const summaryCv = (v) => convertAmount(v, 'GBP', displayCurrency, detail?.fx_rates_used);
  const summaryFmt = (v) => formatAmount(summaryCv(v), displayCurrency);
  const summarySignedFmt = (v) => signedAmount(summaryCv(v), displayCurrency);

  const isPrevDisabled = false;
  const isNextDisabled = year > new Date().getFullYear() ||
    (year === new Date().getFullYear() && month >= new Date().getMonth() + 1);

  // Group transactions by category
  const txByCategory = (detail?.transactions || []).reduce((acc, t) => {
    const cat = t.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const outboundInvoices = (detail?.invoices || []).filter((i) => i.type === 'outbound');
  const inboundInvoices = (detail?.invoices || []).filter((i) => i.type === 'inbound');

  const handleDownloadPDF = async () => {
    if (!detail) return;
    setIsGeneratingPDF(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const entityLabel = entityLabelsMap[selectedEntity] || selectedEntity;
      const generatedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
      const PRIMARY = [22, 101, 52];
      const MUTED = [100, 116, 139];
      const PAGE_W = doc.internal.pageSize.getWidth();
      const MARGIN = 14;
      const fmt = (v) => formatAmount(v, reportCurrency);
      const signedFmt = (v) => `${v >= 0 ? '+' : '-'}${fmt(v)}`;

      // ── Header ──
      doc.setFillColor(...PRIMARY);
      doc.rect(0, 0, PAGE_W, 22, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`${detail.month_label} ${year} — Financial Report`, MARGIN, 13);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Entity: ${entityLabel}   |   Generated: ${generatedOn}`, PAGE_W - MARGIN, 13, { align: 'right' });

      // ── Summary ──
      const s = detail.summary;
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Summary', MARGIN, 32);

      autoTable(doc, {
        startY: 35,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Revenue', 'Expenses', 'Gross Profit', 'Tax Paid', 'Net Profit', 'Invoices', 'Transactions']],
        body: [[
          fmt(s.revenue), fmt(s.expenses), signedFmt(s.gross_profit),
          fmt(s.tax_paid), signedFmt(s.net_profit),
          String(s.invoice_count), String(s.transaction_count),
        ]],
        headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 9, fontStyle: 'bold', halign: 'center' },
        columnStyles: {
          2: { textColor: s.gross_profit >= 0 ? [22, 163, 74] : [220, 38, 38] },
          4: { textColor: s.net_profit >= 0 ? [22, 163, 74] : [220, 38, 38] },
        },
        theme: 'grid',
      });

      // ── Outbound invoices (Revenue) ──
      if (outboundInvoices.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text('Revenue Invoices (Outbound)', MARGIN, doc.lastAutoTable.finalY + 10);
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 13,
          margin: { left: MARGIN, right: MARGIN },
          head: [['Invoice #', 'Client', 'Date', 'Due Date', 'Amount', 'Currency', 'Status']],
          body: outboundInvoices.map((i) => [
            i.invoice_number, i.supplier_client_name,
            i.date ? i.date.slice(0, 10) : '',
            i.due_date ? i.due_date.slice(0, 10) : '',
            fmt(i.amount), i.currency,
            i.payment_status?.replace('_', ' ') || '',
          ]),
          headStyles: { fillColor: [22, 163, 74], textColor: 255, fontStyle: 'bold', fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          alternateRowStyles: { fillColor: [240, 253, 244] },
          theme: 'grid',
        });
      }

      // ── Inbound invoices (Expenses) ──
      if (inboundInvoices.length > 0) {
        if (doc.lastAutoTable.finalY > 150) doc.addPage();
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text('Expense Invoices (Inbound)', MARGIN, doc.lastAutoTable.finalY + 10);
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 13,
          margin: { left: MARGIN, right: MARGIN },
          head: [['Invoice #', 'Supplier', 'Date', 'Due Date', 'Amount', 'Currency', 'Status']],
          body: inboundInvoices.map((i) => [
            i.invoice_number, i.supplier_client_name,
            i.date ? i.date.slice(0, 10) : '',
            i.due_date ? i.due_date.slice(0, 10) : '',
            fmt(i.amount), i.currency,
            i.payment_status?.replace('_', ' ') || '',
          ]),
          headStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: 'bold', fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          alternateRowStyles: { fillColor: [254, 242, 242] },
          theme: 'grid',
        });
      }

      // ── Transactions ──
      if (detail.transactions.length > 0) {
        doc.addPage();
        doc.setFillColor(...PRIMARY);
        doc.rect(0, 0, PAGE_W, 22, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Transactions — ${detail.month_label} ${year}`, MARGIN, 13);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text('All Transactions', MARGIN, 30);

        autoTable(doc, {
          startY: 33,
          margin: { left: MARGIN, right: MARGIN },
          head: [['Date', 'Counterparty', 'Description', 'Category', 'Method', 'Amount', 'Currency']],
          body: detail.transactions.map((t) => [
            t.date ? t.date.slice(0, 10) : '',
            t.counterparty || '—',
            t.description || '—',
            categoryLabels[t.category] || t.category || '—',
            t.payment_method?.replace('_', ' ') || '—',
            fmt(t.amount),
            t.currency || 'GBP',
          ]),
          headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 8 },
          bodyStyles: { fontSize: 7.5 },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          theme: 'grid',
        });
      }

      // ── Footer ──
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(...MUTED);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Page ${i} of ${pageCount}  ·  ${detail.month_label} ${year}  ·  Confidential — ${entityLabel}`,
          PAGE_W / 2,
          doc.internal.pageSize.getHeight() - 6,
          { align: 'center' }
        );
      }

      doc.save(`report-${year}-${String(month).padStart(2, '0')}-${selectedEntity}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const s = detail?.summary;

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/reporting">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold tracking-tight">
                  {detail ? `${detail.month_label} ${year}` : '…'}
                </h1>
              </div>
              <p className="text-muted-foreground mt-1">Monthly financial report</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <CurrencyToggle />
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={!detail || isLoading || isGeneratingPDF}
            >
              {isGeneratingPDF ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download PDF
            </Button>

            {/* Month nav */}
            <div className="flex items-center gap-1 border rounded-lg px-2 py-1.5 bg-card">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goMonth(-1)} disabled={isPrevDisabled}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium px-1 min-w-[80px] text-center">
                {detail?.month_label || '…'}
              </span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goMonth(1)} disabled={isNextDisabled}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !detail ? null : (
          <div className="flex flex-col gap-8">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <SummaryCard label="Revenue" value={summaryFmt(s.revenue)} icon={TrendingUp}
                iconBg="bg-green-100 dark:bg-green-900/30" valueClass="text-green-600 dark:text-green-400" />
              <SummaryCard label="Expenses" value={summaryFmt(s.expenses)} icon={TrendingDown}
                iconBg="bg-red-100 dark:bg-red-900/30" valueClass="text-red-600 dark:text-red-400" />
              <SummaryCard label="Gross Profit" value={summarySignedFmt(s.gross_profit)} icon={DollarSign}
                iconBg={s.gross_profit >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}
                valueClass={s.gross_profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} />
              <SummaryCard label="Tax Paid" value={summaryFmt(s.tax_paid)} icon={Receipt}
                iconBg="bg-orange-100 dark:bg-orange-900/30" valueClass="text-orange-600 dark:text-orange-400" />
              <SummaryCard label="Net Profit" value={summarySignedFmt(s.net_profit)} icon={DollarSign}
                iconBg={s.net_profit >= 0 ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-red-100 dark:bg-red-900/30'}
                valueClass={s.net_profit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'} />
            </div>

            {/* Tabs: Invoices / Transactions */}
            <Tabs defaultValue="invoices">
              <TabsList>
                <TabsTrigger value="invoices">
                  Invoices ({detail.invoices.length})
                </TabsTrigger>
                <TabsTrigger value="transactions">
                  Transactions ({detail.transactions.length})
                </TabsTrigger>
              </TabsList>

              {/* ── Invoices Tab ── */}
              <TabsContent value="invoices" className="mt-6">
                <div className="flex flex-col gap-6">
                  {/* Outbound */}
                  <div>
                    <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-green-600" />
                      Revenue Invoices
                      <span className="text-sm font-normal text-muted-foreground">({outboundInvoices.length})</span>
                    </h3>
                    {outboundInvoices.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">No outbound invoices this month.</p>
                    ) : (
                      <Card>
                        <div className="overflow-x-auto">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Invoice #</th>
                                <th>Client</th>
                                <th>Date</th>
                                <th>Due Date</th>
                                <th>Amount</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {outboundInvoices.map((inv) => (
                                <motion.tr key={inv.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                  <td className="font-mono text-xs">{inv.invoice_number}</td>
                                  <td className="font-medium">{inv.supplier_client_name}</td>
                                  <td>{inv.date ? format(new Date(inv.date), 'dd MMM yyyy') : '—'}</td>
                                  <td>{inv.due_date ? format(new Date(inv.due_date), 'dd MMM') : '—'}</td>
                                  <td className="font-mono font-semibold text-green-600">
                                    {CURRENCY_SYMBOLS[inv.currency] || inv.currency}
                                    {Math.abs(inv.amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                  </td>
                                  <td>
                                    <Badge className={cn('text-xs', statusColors[inv.payment_status] || '')}>
                                      {inv.payment_status?.replace('_', ' ')}
                                    </Badge>
                                  </td>
                                </motion.tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    )}
                  </div>

                  {/* Inbound */}
                  <div>
                    <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-red-600" />
                      Expense Invoices
                      <span className="text-sm font-normal text-muted-foreground">({inboundInvoices.length})</span>
                    </h3>
                    {inboundInvoices.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">No inbound invoices this month.</p>
                    ) : (
                      <Card>
                        <div className="overflow-x-auto">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Invoice #</th>
                                <th>Supplier</th>
                                <th>Date</th>
                                <th>Due Date</th>
                                <th>Amount</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inboundInvoices.map((inv) => (
                                <motion.tr key={inv.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                  <td className="font-mono text-xs">{inv.invoice_number}</td>
                                  <td className="font-medium">{inv.supplier_client_name}</td>
                                  <td>{inv.date ? format(new Date(inv.date), 'dd MMM yyyy') : '—'}</td>
                                  <td>{inv.due_date ? format(new Date(inv.due_date), 'dd MMM') : '—'}</td>
                                  <td className="font-mono font-semibold text-red-600">
                                    {CURRENCY_SYMBOLS[inv.currency] || inv.currency}
                                    {Math.abs(inv.amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                  </td>
                                  <td>
                                    <Badge className={cn('text-xs', statusColors[inv.payment_status] || '')}>
                                      {inv.payment_status?.replace('_', ' ')}
                                    </Badge>
                                  </td>
                                </motion.tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ── Transactions Tab ── */}
              <TabsContent value="transactions" className="mt-6">
                <div className="flex flex-col gap-6">
                  {Object.keys(txByCategory).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">No transactions this month.</p>
                  ) : (
                    Object.entries(txByCategory).map(([cat, txns]) => (
                      <div key={cat}>
                        <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-primary" />
                          <Badge className={cn('text-xs', categoryColors[cat] || categoryColors.other)}>
                            {categoryLabels[cat] || cat}
                          </Badge>
                          <span className="text-sm font-normal text-muted-foreground">({txns.length})</span>
                          <span className="ml-auto text-sm font-mono text-muted-foreground">
                            Total: {fmt(txns.reduce((sum, t) => sum + Math.abs(t.amount), 0))}
                          </span>
                        </h3>
                        <Card>
                          <div className="overflow-x-auto">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Counterparty</th>
                                  <th>Description</th>
                                  <th>Method</th>
                                  <th>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {txns.map((t) => (
                                  <motion.tr key={t.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                    <td>{t.date ? format(new Date(t.date), 'dd MMM yyyy') : '—'}</td>
                                    <td className="font-medium">{t.counterparty || '—'}</td>
                                    <td className="max-w-[200px] truncate text-muted-foreground">{t.description}</td>
                                    <td>
                                      {t.payment_method && (
                                        <Badge variant="outline" className="text-xs capitalize">
                                          {t.payment_method.replace('_', ' ')}
                                        </Badge>
                                      )}
                                    </td>
                                    <td className="font-mono font-medium">
                                      {CURRENCY_SYMBOLS[t.currency] || t.currency}
                                      {Math.abs(t.amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                    </td>
                                  </motion.tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Card>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
